import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState } from "@/components/admin/ui";
import { Handshake } from "lucide-react";
import { LeadList } from "./LeadList";
import { LeadDetailTabs } from "./LeadDetailTabs";
import { ActivityPanel } from "./ActivityPanel";
import { LEAD_STATUS_META } from "./salesMeta";

export const SalesWorkspace = () => {
  const { API } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [meta, setMeta] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedId, setSelectedId] = useState(searchParams.get("lead") || null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "", team_id: "" });

  const loadMeta = useCallback(() => {
    axios.get(`${API}/org/sales/meta`, { withCredentials: true }).then(({ data }) => setMeta(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  const loadLeads = useCallback(() => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.status) params.status = filters.status;
    if (filters.team_id) params.team_id = filters.team_id;
    axios.get(`${API}/org/sales/leads`, { params, withCredentials: true }).then(({ data }) => setLeads(data.leads)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, filters]);

  const loadDetail = useCallback((leadId) => {
    if (!leadId) { setDetail(null); return; }
    axios.get(`${API}/org/sales/leads/${leadId}`, { withCredentials: true }).then(({ data }) => setDetail(data)).catch((err) => { setError(formatApiErrorDetail(err.response?.data?.detail)); setDetail(null); });
  }, [API]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const selectLead = (leadId) => {
    setSelectedId(leadId);
    setSearchParams(leadId ? { lead: leadId } : {});
  };

  const refreshDetail = async () => { loadDetail(selectedId); loadLeads(); };

  if (!meta || !leads) {
    return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="sales-loading">Loading…</p>;
  }

  const statusMeta = detail ? (LEAD_STATUS_META[detail.lead.status] || { label: detail.lead.status, cls: "" }) : null;

  return (
    <div data-testid="sales-workspace">
      {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="sales-error">{error}</p>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr_2fr] lg:h-[calc(100vh-220px)]">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#111C33] lg:overflow-hidden">
          <LeadList leads={leads} meta={meta} selectedId={selectedId} onSelect={selectLead} filters={filters} onFilterChange={(k, v) => setFilters((f) => ({ ...f, [k]: v }))} />
        </div>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#111C33] lg:overflow-y-auto">
          {!detail ? (
            <EmptyState icon={Handshake} title="Select a Lead" message="Choose a Lead from the list to review and work it." testId="sales-detail-empty" />
          ) : (
            <>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold tracking-tight text-slate-950 dark:text-white" data-testid="lead-detail-name">{detail.lead.first_name} {detail.lead.last_name}</h2>
                  <p className="font-mono text-xs text-slate-400" data-testid="lead-detail-display-id">{detail.lead.display_id}</p>
                </div>
                <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusMeta.cls}`} data-testid="lead-detail-status-badge">{statusMeta.label}</span>
              </div>
              <LeadDetailTabs
                lead={detail.lead}
                customer={detail.customer}
                intakeAttachments={detail.intake_attachments}
                channels={detail.channels}
                meta={meta}
                eligibleTeams={detail.eligible_teams || []}
                eligibleLeadOwners={detail.eligible_lead_owners || []}
                businessItems={detail.business_items || []}
                onRefresh={refreshDetail}
                onError={setError}
                API={API}
              />
            </>
          )}
        </div>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#111C33] lg:overflow-hidden">
          {detail ? (
            <ActivityPanel lead={detail.lead} channels={detail.channels} onRefresh={refreshDetail} onError={setError} API={API} />
          ) : (
            <p className="text-sm text-slate-400">Select a Lead to see its activity.</p>
          )}
        </div>
      </div>
    </div>
  );
};
